/**
 * Zod schema for the OT-type create/edit form (Phase 26 / PAY2-04, be-207).
 *
 * Business rules:
 *   - nameEn / nameAr: required, max 120 chars
 *   - multiplier: [1.000, 5.000] inclusive, scale 3. 1.000 is the only hard
 *     floor — overtime can never pay less than normal time. The statutory
 *     reference rates (1.250 regular, 1.500 rest/holiday, Jordan Labour Law
 *     Art. 59) are GUIDANCE, not limits: a company may configure a lower rate
 *     and owns that compliance decision. The server reports the reference and
 *     the breach on `OvertimeTypeView.statutoryMinimumMultiplier` /
 *     `.belowStatutoryMinimum`, which the form shows as a non-blocking notice.
 *   - code: NOT in the FE form (server-generated monotonic OT-001, OT-002, ...)
 *   - eligibilityRules: optional, free JSON (v1 omitted from form)
 *
 * Exported as a named export so both the form component and the contract
 * tests import from a single source. The inferred `OtTypeFormValues` type
 * is the RHF form data shape.
 */
import { z } from "zod";

export const otTypeFormSchema = z.object({
  // Messages are i18n KEYS (resolved at the form render by the consumer), so
  // Arabic admins don't see hardcoded English validation copy.
  nameEn: z
    .string()
    .min(1, "ot-types:validation.name_en_required")
    .max(120, "ot-types:validation.name_max"),
  nameAr: z
    .string()
    .min(1, "ot-types:validation.name_ar_required")
    .max(120, "ot-types:validation.name_max"),
  category: z.enum(["REGULAR_OVERTIME", "REST_OR_HOLIDAY"]).default("REGULAR_OVERTIME"),
  multiplier: z
    .number({ invalid_type_error: "ot-types:validation.multiplier_number" })
    .min(1.0, "ot-types:validation.multiplier_min")
    .max(5.0, "ot-types:validation.multiplier_max")
    .refine((n) => Math.round(n * 1000) === n * 1000, "ot-types:validation.multiplier_decimals"),
});

export type OtTypeFormValues = z.infer<typeof otTypeFormSchema>;
